import React, { useEffect, useState } from 'react';
import { Facebook, Instagram, Linkedin, Music2, Twitter, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

/**
 * Site-wide social media icon row.
 * - Pulls links from /api/social-links
 * - Hides any link that is disabled, missing, or still using an ADD_FINAL_*_URL placeholder
 * - All external links open in a new tab with rel="noopener noreferrer"
 */

const PLACEHOLDER_PATTERN = /^ADD_FINAL_[A-Z]+_URL$/i;

function isLiveUrl(url) {
  if (!url) return false;
  const trimmed = String(url).trim();
  if (!trimmed) return false;
  if (PLACEHOLDER_PATTERN.test(trimmed)) return false;
  // Must be a real https/http URL
  return /^https?:\/\//i.test(trimmed);
}

function iconFor(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('instagram')) return Instagram;
  if (n.includes('facebook')) return Facebook;
  if (n.includes('linkedin')) return Linkedin;
  if (n.includes('tiktok')) return Music2;
  if (n.includes('twitter') || n === 'x') return Twitter;
  return LinkIcon;
}

function labelFor(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('instagram')) return 'Instagram';
  if (n.includes('facebook')) return 'Facebook';
  if (n.includes('linkedin')) return 'LinkedIn';
  if (n.includes('tiktok')) return 'TikTok';
  if (n.includes('twitter') || n === 'x') return 'X (Twitter)';
  return name || 'Social';
}

export default function SocialLinks({ className = '', iconClassName = 'h-4 w-4' }) {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/social-links`);
        if (cancelled) return;
        const fetched = (res.data?.links || []).filter(
          (l) => l && l.enabled !== false && isLiveUrl(l.url)
        );
        setLinks(fetched);
      } catch {
        if (!cancelled) setLinks([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!links.length) return null;

  return (
    <ul className={`flex items-center gap-3 ${className}`} data-testid="social-links">
      {links.map((link) => {
        const Icon = iconFor(link.name);
        const label = labelFor(link.name);
        return (
          <li key={`${link.name}-${link.url}`}>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`OnSiteHand on ${label}`}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              data-testid={`social-link-${(link.name || '').toLowerCase()}`}
            >
              <Icon className={iconClassName} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
